import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import {
  getApiError,
  materialUsageApi,
  type ConsumableProduct,
  type MaterialUsageDetail,
  type MaterialUsageDraftLine,
  type MaterialUsageLineInput,
} from '../../lib/api.js';
import { formatHourMinute } from '../../lib/time-zone.js';

/// O previsto de um produto no protocolo escolhido, já somado entre as etapas.
export interface PlannedClosingMaterial {
  productId: string;
  quantity: string;
}

export interface ClosingLine {
  key: string;
  productId: string;
  quantity: string;
  locationId: string;
  lotId: string;
}

type LineErrors = Partial<Record<'productId' | 'quantity', string>>;

export interface ClosingMaterials {
  loading: boolean;
  error: unknown;
  detail: MaterialUsageDetail | undefined;
  catalog: readonly ConsumableProduct[];
  locationOptions: Array<{ value: string; label: string }>;
  lines: ClosingLine[];
  errors: Record<string, LineErrors>;
  requestError: string | undefined;
  /// Quem salvou o rascunho por último e quando. Nulo sem rascunho gravado.
  draftInfo: { by: string; at: string } | null;
  saving: boolean;
  lotOptionsOf: (line: ClosingLine) => Array<{ value: string; label: string }>;
  lotOf: (line: ClosingLine) => string;
  update: (key: string, patch: Partial<ClosingLine>) => void;
  add: () => void;
  remove: (key: string) => void;
  /// Volta o material ao previsto do protocolo escolhido. Usado quando o protocolo muda.
  resetTo: (planned: readonly PlannedClosingMaterial[] | null) => void;
  discard: () => Promise<void>;
  /// Registra o que está no rascunho e baixa do estoque. Falso quando algo foi recusado: nada grava.
  register: () => Promise<boolean>;
}

const usageKey = ['company', 'material-usage'] as const;
const SAVE_DELAY_MS = 800;
let nextKey = 0;
const keyOf = () => `line-${String((nextKey += 1))}`;

/// O material do fechamento (ADR 0030): nasce do rascunho gravado ou, sem ele, do previsto do
/// protocolo escolhido. Toda mudança salva sozinha como rascunho, que não move saldo; registrar
/// transforma o rascunho em consumo e o apaga.
export function useClosingMaterials({
  appointmentId,
  enabled,
  planned,
  plannedReady,
  canRegister: allowed,
  timeZone,
}: {
  appointmentId: string;
  /// Falso sem Consumo de materiais contratado ou sem permissão de ler: nada é pedido à API.
  enabled: boolean;
  /// Nulo quando não há previsto a oferecer: sem protocolo, ou sem Protocolos contratado.
  planned: readonly PlannedClosingMaterial[] | null;
  /// O previsto já chegou (ou não virá). O formulário só nasce depois, para não abrir vazio.
  plannedReady: boolean;
  canRegister: boolean;
  timeZone: string;
}): ClosingMaterials {
  const canRegister = enabled && allowed;
  const queryClient = useQueryClient();
  const detail = useQuery({
    queryKey: [...usageKey, 'detail', appointmentId],
    queryFn: () => materialUsageApi.get(appointmentId),
    enabled,
    retry: false,
  });
  const draft = useQuery({
    queryKey: [...usageKey, 'draft', appointmentId],
    queryFn: () => materialUsageApi.getDraft(appointmentId),
    enabled: canRegister,
    retry: false,
  });
  const products = useQuery({
    queryKey: [...usageKey, 'products'],
    queryFn: () => materialUsageApi.listProducts(),
    enabled: canRegister,
    retry: false,
  });
  const locations = useQuery({
    queryKey: [...usageKey, 'locations'],
    queryFn: () => materialUsageApi.listLocations(),
    enabled: canRegister,
    retry: false,
  });
  const settings = useQuery({
    queryKey: [...usageKey, 'settings'],
    queryFn: () => materialUsageApi.getSettings(),
    enabled: canRegister,
    retry: false,
  });
  /// Sem padrão escolhido, o único local da unidade já resolve: não há o que perguntar.
  const onlyLocationId = locations.data?.length === 1 ? locations.data[0]!.id : '';
  const defaultLocationId = settings.data?.defaultLocationId ?? onlyLocationId;
  /// Nulo enquanto ninguém mexeu: o formulário é o que o servidor diz. A primeira mudança o
  /// torna próprio desta tela, e dali em diante o rascunho segue o que está aqui.
  const [edited, setEdited] = useState<ClosingLine[] | null>(null);
  const [errors, setErrors] = useState<Record<string, LineErrors>>({});
  const [requestError, setRequestError] = useState<string>();
  /// Indefinido é "o do servidor"; nulo é "não há rascunho".
  const [info, setInfo] = useState<{ by: string; at: string } | null | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const infoOf = (saved: { savedByDisplayName: string; savedAt: string }) => ({
    by: saved.savedByDisplayName,
    at: formatHourMinute(saved.savedAt, timeZone),
  });

  const ready =
    canRegister &&
    plannedReady &&
    detail.isSuccess &&
    draft.isSuccess &&
    settings.isSuccess &&
    products.isSuccess &&
    locations.isSuccess;

  /// Enquanto ninguém mexeu, o formulário é o rascunho gravado ou, sem ele, o previsto quando o
  /// atendimento ainda não tem material registrado.
  const liveLines = (detail.data?.lines ?? []).filter((line) => !line.reversedAt).length;
  const base: ClosingLine[] = !ready
    ? []
    : draft.data
      ? draft.data.lines.map((line, index) =>
          fromDraft(line, defaultLocationId, `draft-${String(index)}`),
        )
      : liveLines === 0
        ? fromPlanned(planned, defaultLocationId, 'planned')
        : [];
  const draftInfo = info === undefined ? (draft.data ? infoOf(draft.data) : null) : info;

  useEffect(() => () => clearTimeout(timer.current), []);

  function persist(next: ClosingLine[]) {
    clearTimeout(timer.current);
    setSaving(true);
    timer.current = setTimeout(() => {
      void materialUsageApi
        .saveDraft(appointmentId, next.map(toDraft))
        .then((saved) => setInfo(infoOf(saved)))
        .catch((cause: unknown) => setRequestError(getApiError(cause)))
        .finally(() => setSaving(false));
    }, SAVE_DELAY_MS);
  }

  function change(next: ClosingLine[]) {
    setEdited(next);
    persist(next);
  }

  /// As sugestões de lote de todas as linhas rastreadas numa consulta só: o lote sugerido é valor
  /// derivado, e não um segundo estado a sincronizar.
  const catalog = products.data ?? [];
  const current = edited ?? base;
  const lotTargets = current
    .filter((line) => {
      const product = catalog.find((item) => item.id === line.productId);
      return product?.requiresLot && (line.locationId || defaultLocationId) && line.quantity.trim();
    })
    .map((line) => ({
      key: line.key,
      productId: line.productId,
      locationId: line.locationId || defaultLocationId,
      quantity: line.quantity.trim().replace(',', '.'),
    }));
  const lots = useQuery({
    queryKey: [
      ...usageKey,
      'lots',
      lotTargets.map(
        (target) => `${target.key}:${target.productId}:${target.locationId}:${target.quantity}`,
      ),
    ],
    queryFn: async () =>
      Object.fromEntries(
        await Promise.all(
          lotTargets.map(
            async (target) =>
              [
                target.key,
                await materialUsageApi
                  .suggestLots({
                    productId: target.productId,
                    locationId: target.locationId,
                    quantity: target.quantity,
                  })
                  .catch(() => []),
              ] as const,
          ),
        ),
      ),
    enabled: lotTargets.length > 0,
    retry: false,
  });

  function lotOf(line: ClosingLine): string {
    if (line.lotId) return line.lotId;
    return (lots.data?.[line.key] ?? []).find((lot) => lot.suggested)?.id ?? '';
  }

  async function register(): Promise<boolean> {
    if (current.length === 0) return true;
    const found: Record<string, LineErrors> = {};
    for (const line of current) {
      const lineErrors: LineErrors = {};
      if (!line.productId) lineErrors.productId = 'Escolha o produto.';
      if (!line.quantity.trim()) lineErrors.quantity = 'Informe quanto foi usado.';
      if (Object.keys(lineErrors).length) found[line.key] = lineErrors;
    }
    setErrors(found);
    if (Object.keys(found).length) {
      setRequestError('Nenhum material foi gravado. Corrija as linhas marcadas.');
      return false;
    }
    setRequestError(undefined);
    clearTimeout(timer.current);
    try {
      const input: MaterialUsageLineInput[] = current.map((line) => ({
        productId: line.productId,
        quantity: line.quantity,
        ...(line.locationId ? { locationId: line.locationId } : {}),
        ...(lotOf(line) ? { lotId: lotOf(line) } : {}),
      }));
      await materialUsageApi.register(appointmentId, input);
      setEdited([]);
      setInfo(null);
      setSaving(false);
      await queryClient.invalidateQueries({ queryKey: usageKey });
      return true;
    } catch (cause) {
      setRequestError(`Nenhum material foi gravado. ${getApiError(cause)}`);
      return false;
    }
  }

  return {
    loading:
      detail.isLoading ||
      (canRegister &&
        (draft.isLoading || products.isLoading || locations.isLoading || settings.isLoading)),
    error: detail.error ?? draft.error ?? products.error ?? locations.error ?? settings.error,
    detail: detail.data,
    catalog,
    locationOptions: (locations.data ?? []).map((location) => ({
      value: location.id,
      label: location.displayName,
    })),
    lines: current,
    errors,
    requestError,
    draftInfo,
    saving,
    lotOptionsOf: (line) =>
      (lots.data?.[line.key] ?? []).map((lot) => ({
        value: lot.id,
        label: `${lot.code} · saldo ${lot.balance}${lot.suggested ? ' · sugerido' : ''}`,
      })),
    lotOf,
    update: (key, patch) => {
      setErrors((previous) => {
        const next = { ...previous };
        delete next[key];
        return next;
      });
      change(current.map((line) => (line.key === key ? { ...line, ...patch } : line)));
    },
    add: () =>
      change([
        ...current,
        { key: keyOf(), productId: '', quantity: '', locationId: defaultLocationId, lotId: '' },
      ]),
    remove: (key) => change(current.filter((line) => line.key !== key)),
    resetTo: (next) => {
      if (!ready) return;
      setErrors({});
      change(fromPlanned(next, defaultLocationId));
    },
    discard: async () => {
      clearTimeout(timer.current);
      setSaving(false);
      await materialUsageApi.discardDraft(appointmentId);
      setInfo(null);
      setErrors({});
      setRequestError(undefined);
      setEdited(liveLines === 0 ? fromPlanned(planned, defaultLocationId) : []);
    },
    register,
  };
}

/// Com prefixo, a chave é estável entre renders: o formulário derivado não remonta os campos a cada
/// vez que é recalculado.
function fromPlanned(
  planned: readonly PlannedClosingMaterial[] | null,
  defaultLocationId: string,
  prefix?: string,
): ClosingLine[] {
  return (planned ?? []).map((item, index) => ({
    key: prefix ? `${prefix}-${String(index)}` : keyOf(),
    productId: item.productId,
    quantity: item.quantity.replace('.', ','),
    locationId: defaultLocationId,
    lotId: '',
  }));
}

function fromDraft(
  line: MaterialUsageDraftLine,
  defaultLocationId: string,
  key: string,
): ClosingLine {
  return {
    key,
    productId: line.productId ?? '',
    quantity: line.quantity,
    locationId: line.locationId ?? defaultLocationId,
    lotId: line.lotId ?? '',
  };
}

function toDraft(line: ClosingLine): MaterialUsageDraftLine {
  return {
    productId: line.productId || null,
    quantity: line.quantity,
    locationId: line.locationId || null,
    lotId: line.lotId || null,
    note: null,
  };
}
